import { NewRequisitionForm } from "../../../recoil/types";
import {
  getRelationshipToInsured,
  normalizeMedicalRecordNumber,
} from "../helpers";
import {
  DRAFT_STATUS,
  NewRequisitionType,
  ShippingInformationRequest,
} from "../types";

const buildShippingAddress = (
  form: NewRequisitionForm,
): ShippingInformationRequest => {
  if (form.sameAsPatientShippingAddress) {
    return {
      first_name: form.firstName,
      last_name: form.lastName,
      street_address: form.streetAddress,
      apt_suite: form.aptUnit,
      city: form.city,
      state: form.state.split("-")[0].trim(),
      zip_code: form.zipCode,
    };
  }
  return {
    first_name: form.shippingFirstName,
    last_name: form.shippingLastName,
    street_address: form.shippingStreetAddress,
    apt_suite: form.shippingAptUnit,
    city: form.shippingCity,
    state: form.shippingState.split("-")[0].trim(),
    zip_code: form.shippingZipCode,
  };
};

const buildShipmentFields = (
  form: NewRequisitionForm,
  shipKitEnabled: boolean,
): {
  is_ship_to_patient: boolean;
  shipping_information: ShippingInformationRequest | undefined;
} => {
  if (!shipKitEnabled || !form.isShipToPatient) {
    return { is_ship_to_patient: false, shipping_information: undefined };
  }
  const shippingInformation = buildShippingAddress(form);
  // The kit-to-patient feature is still in testing so we're not doing full validation
  // This block just ignores shipping info if it's incomplete so we don't break the order
  const requiredFields: (keyof ShippingInformationRequest)[] = [
    "first_name",
    "last_name",
    "street_address",
    "city",
    "state",
    "zip_code",
  ];
  const hasAllRequiredFields = requiredFields.every(
    (field) => shippingInformation[field].trim() !== "",
  );
  if (!hasAllRequiredFields) {
    return { is_ship_to_patient: false, shipping_information: undefined };
  }
  return {
    is_ship_to_patient: true,
    shipping_information: shippingInformation,
  };
};

const ORDERING_PROVIDER_SIGNATURE_PREFIXES = [
  "Electronically signed by ordering provider:",
  "Signed and submitted by ordering provider:",
];

const ORDERING_PROVIDER_SIGNATURE_SEPARATOR = "\n \n \n";

export const stripOrderingProviderSignature = (
  treatmentNotes: string,
): string => {
  const signaturePattern = new RegExp(
    `(?:\\n\\s*)*(?:${ORDERING_PROVIDER_SIGNATURE_PREFIXES.join("|")}).*\\s*$`,
  );
  return treatmentNotes.replace(signaturePattern, "").trimEnd();
};

export const appendOrderingProviderSignature = (
  treatmentNotes: string,
  orderingHealthcareProviderFormatted: string,
): string => {
  const unsignedTreatmentNotes =
    stripOrderingProviderSignature(treatmentNotes).trimEnd();
  const signature = `Electronically signed by ordering provider: ${orderingHealthcareProviderFormatted}`;
  if (!unsignedTreatmentNotes) return signature;
  return `${unsignedTreatmentNotes}${ORDERING_PROVIDER_SIGNATURE_SEPARATOR}${signature}`;
};

export const convertNewReqFormToRequest = (
  form: NewRequisitionForm,
  shipKitEnabled = false,
): NewRequisitionType => ({
  // patient info
  first_name: form.firstName,
  middle_initial: form.middleInitial,
  last_name: form.lastName,
  sex: form.sex,
  maternal_weight: form.maternalWeight.toString(),
  medical_record_number: normalizeMedicalRecordNumber(
    form.medicalRecordNumber || "",
  ),
  // don't format these dates since the backend needs a certain format
  date_of_birth: form.dateOfBirth,
  collection_date: form.collectionDate || undefined,
  estimated_due_date: form.estimatedDueDate || null,
  cell_phone: form.cellPhone,
  email_address: form.emailAddress,
  street_address: form.streetAddress,
  apt_suite: form.aptUnit,
  city: form.city,
  state: form.state.split("-")[0].trim(),
  zip_code: form.zipCode,
  is_asian: form.asian,
  is_african: form.african,
  is_ashkenazi: form.ashkenazi,
  is_middle_eastern: form.middleEastern,
  is_french_canadian: form.frenchCanadian,
  is_hispanic: form.hispanic,
  is_northern_european: form.northernEuropean,
  is_white: form.white,
  is_other_ethnicity: form.otherEthnicity,
  is_unknown_ethnicity: form.unknownEthnicity,
  family_history_of_genetic_disorders: form.familyHistoryGeneticDisorders,
  is_reported_carrier_patient_alpha: form.reportedCarrierPatientAlpha,
  is_reported_carrier_patient_sickle: form.reportedCarrierPatientSickle,
  is_reported_carrier_patient_SMA: form.reportedCarrierPatientSMA,
  is_reported_carrier_patient_cf: form.reportedCarrierPatientCF,
  reported_carrier_patient_variant: form.reportedCarrierPatientVariant,
  is_reported_carrier_fob_alpha: form.reportedCarrierFOBAlpha,
  is_reported_carrier_fob_sickle: form.reportedCarrierFOBSickle,
  is_reported_carrier_fob_SMA: form.reportedCarrierFOBSMA,
  is_reported_carrier_fob_cf: form.reportedCarrierFOBCF,
  reported_carrier_fob_variant: form.reportedCarrierFOBVariant,
  reproductive_partner_first_name: form.reproductivePartnerFirstName,
  reproductive_partner_last_name: form.reproductivePartnerLastName,
  reproductive_partner_date_of_birth:
    form.reproductivePartnerDateOfBirth || null,
  is_reproductive_partner_maternal: form.maternalReproductivePartner,
  is_reproductive_partner_paternal: form.paternalReproductivePartner,
  is_carrier_screen_order: form.carrierScreen,
  expanded_carrier_panel: form.expandedCarrierPanel,
  is_rhd_only_order: form.isRhdOnlyOrder,
  is_aneuploidy_order: form.aneuploidyNIPT,
  is_aneuploidy_twin_nipt: form.aneuploidyTwinNIPT,
  is_aneuploidy_opt_out_fetal_sex: form.aneuploidyOptOutFetalSex,
  is_aneuploidy_include_rhd: form.aneuploidyIncludeRhd,
  is_fragile_x: form.isFragileX,
  is_acog_plus: form.isAcogPlus,
  is_fetal_antigen: form.isFetalAntigen,
  is_fetal_antigen_rbc: form.isFetalAntigenRbc,
  is_fetal_antigen_platelet: form.isFetalAntigenPlatelet,
  is_fetal_antigen_big_c: form.isFetalAntigenBigC,
  is_fetal_antigen_little_c: form.isFetalAntigenLittleC,
  is_fetal_antigen_d: form.isFetalAntigenD,
  is_fetal_antigen_duffy: form.isFetalAntigenDuffy,
  is_fetal_antigen_e: form.isFetalAntigenE,
  is_fetal_antigen_kell: form.isFetalAntigenKell,
  is_fetal_antigen_little_e: form.isFetalAntigenLittleE,
  is_fetal_antigen_fyb: form.isFetalAntigenFyb,
  is_fetal_antigen_jka: form.isFetalAntigenJka,
  is_fetal_antigen_jkb: form.isFetalAntigenJkb,
  is_fetal_antigen_little_k: form.isFetalAntigenLittleK,
  is_fetal_antigen_m: form.isFetalAntigenM,
  is_fetal_antigen_n: form.isFetalAntigenN,
  is_fetal_antigen_big_s: form.isFetalAntigenBigS,
  is_fetal_antigen_little_s: form.isFetalAntigenLittleS,
  is_fetal_antigen_u: form.isFetalAntigenU,
  is_fetal_antigen_hpa_1a: form.isFetalAntigenHpa1a,
  is_fetal_antigen_hpa_1b: form.isFetalAntigenHpa1b,
  is_fetal_antigen_hpa_2a: form.isFetalAntigenHpa2a,
  is_fetal_antigen_hpa_2b: form.isFetalAntigenHpa2b,
  is_fetal_antigen_hpa_3a: form.isFetalAntigenHpa3a,
  is_fetal_antigen_hpa_3b: form.isFetalAntigenHpa3b,
  is_fetal_antigen_hpa_4a: form.isFetalAntigenHpa4a,
  is_fetal_antigen_hpa_4b: form.isFetalAntigenHpa4b,
  is_fetal_antigen_hpa_5a: form.isFetalAntigenHpa5a,
  is_fetal_antigen_hpa_5b: form.isFetalAntigenHpa5b,
  is_fetal_antigen_hpa_9a: form.isFetalAntigenHpa9a,
  is_fetal_antigen_hpa_9b: form.isFetalAntigenHpa9b,
  is_fetal_antigen_hpa_15a: form.isFetalAntigenHpa15a,
  is_fetal_antigen_hpa_15b: form.isFetalAntigenHpa15b,
  is_pregnant: !form.notPregnant,
  is_twin: form.isTwin,
  is_triplet_or_more: form.isTripleOrMore,
  is_egg_donor: form.isEggDonor,
  age_of_egg_donor: form.ageOfEggDonor,
  is_Z84_81: form.Z8481,
  is_Z31_430: form.Z31430,
  is_Z31_440: form.Z31440,
  is_Z81_0: form.Z810,
  is_O28_5_carrier: form.O285_carrier,
  is_Z34_81_carrier: form.Z3481_carrier,
  is_Z34_82_carrier: form.Z3482_carrier,
  is_Z34_83_carrier: form.Z3483_carrier,
  opt_out_cf_dna: form.cfDNAOptOut,
  // aneuploidy nipt
  is_O09_511: form.O09511,
  is_O09_512: form.O09512,
  is_O09_513: form.O09513,
  is_O09_521: form.O09521,
  is_O09_522: form.O09522,
  is_O09_523: form.O09523,
  is_O09_891: form.O09891,
  is_O09_892: form.O09892,
  is_O09_893: form.O09893,
  is_O28_5_aneuploidy: form.O285_aneuploidy,
  is_Z34_81_aneuploidy: form.Z3481_aneuploidy,
  is_Z34_82_aneuploidy: form.Z3482_aneuploidy,
  is_Z34_83_aneuploidy: form.Z3483_aneuploidy,
  is_Z34_01: form.Z3401,
  is_aneuploidy_include_22q: form.is22q,
  is_O28_3: form.O283,
  is_vanishing_twin: form.vanishingTwin,
  abnormal_ultrasound_findings: form.abnormalUltrasoundFindings,
  is_Z34_02: form.Z3402,
  is_Z34_03: form.Z3403,
  is_Z36_0: form.Z360,
  // rhd nipt
  is_O36_0110: form.O360110,
  is_O36_0120: form.O360120,
  is_O36_0130: form.O360130,
  is_Z31_82: form.Z3182,
  // fetal antigen nipt
  is_O36_1910: form.O361910,
  is_O36_1920: form.O361920,
  is_O36_1930: form.O361930,
  // (comma separated values if more than one apply)
  other_icd_10_codes: form.otherICDCodes,
  other_carrier_screen_icd_10_codes: form.otherCarrierScreenICDCodes,
  other_aneuploidy_icd_10_codes: form.otherAneuploidyICDCodes,
  other_rhd_icd_10_codes: form.otherRhdICDCodes,
  other_fetal_antigen_icd_10_codes: form.otherFetalAntigenICDCodes,
  // ship kit to patient
  ...buildShipmentFields(form, shipKitEnabled),
  // billing info
  is_provider_authorized: form.providerAuthorization,
  is_standard_account: form.isStandardAccount,
  is_international_account: form.isInternationalAccount,
  is_mobile_phlebotomy_account: form.isMobilePhlebotomyAccount,
  is_patient_sheet_account: form.isPatientSheetAccount,
  is_insurance_sheet_account: form.isInsuranceSheetAccount,
  is_fetal_antigen_account: form.isFetalAntigenAccount,
  is_fragile_x_account: form.isFragileXAccount,
  is_rhd_only_account: form.isRhdOnlyAccount,
  is_130_panel_account: form.is130PanelAccount,
  is_611_panel_account: form.is611PanelAccount,
  is_113_custom_panel_account: form.is113CustomPanelAccount,
  is_mobile_phlebotomy: form.isMobilePhlebotomy,
  is_bill_to_insurance: form.billToInsurance,
  is_bill_to_patient: form.billToPatient,
  is_bill_to_client: form.billToClient,
  is_pending_medicaid: form.billToPendingMedicaid,
  is_cash_pay: false,
  bill_to_insurance_type: form.billToInsuranceType,
  ins_card_urls: [
    ...Object.keys(form.insuranceFileUrls).map(
      (file) => form.insuranceFileUrls[file].fileName,
    ),
    ...Object.keys(form.insuranceFileUrlsSecondaryFront).map(
      (file) => form.insuranceFileUrlsSecondaryFront[file].fileName,
    ),
    ...Object.keys(form.insuranceFileUrlsSecondaryBack).map(
      (file) => form.insuranceFileUrlsSecondaryBack[file].fileName,
    ),
  ],
  fetal_antigen_urls: Object.keys(form.antigenFileUrls).map(
    (file) => form.antigenFileUrls[file].fileName,
  ),
  insurance_company_name: form.insuranceCompanyName,
  group_id: form.groupId,
  member_id: form.memberId,
  policy_owner_first_name: form.policyOwnerFirstName,
  policy_owner_last_name: form.policyOwnerLastName,
  policy_owner_dob: form.policyOwnerDob || null,
  preferred_language:
    form.preferredLanguage === "Other"
      ? form.otherLanguage
      : form.preferredLanguage,
  relationship_to_insured: getRelationshipToInsured(
    form.relationshipToPolicyOwnerSelf,
    form.relationshipToPolicyOwnerDependent,
    form.relationshipToPolicyOwnerOther,
  ),
  lims_id: form.clinicAccountNumber,
  npi: form.orderingHealthcareNPI,
  ordering_health_care_providers: form.orderingHealthcareProviders,
  clinic_phone: form.clinicPhone,
  clinic_fax: form.clinicFax,
  clinic_name: form.clinicName,
  clinic_additional_notes: form.clinicAdditionalNotes,
  unity_complete_id: form.unityCompleteId || "",
  is_patient_sheet_attached: form.patientSheetAttached,
  is_ins_sheet_attached: form.insuranceSheetAttached,

  treatment_notes: appendOrderingProviderSignature(
    form.treatmentNotes,
    form.orderingHealthcareProviderFormatted,
  ),
});

const getUploadFileName = (fileUrl: string, index: number): string => {
  const urlWithoutQuery = fileUrl.split("?")[0];
  const fileName = urlWithoutQuery.split("/").pop();
  return fileName || `uploaded-file-${index + 1}`;
};

const convertFileUrlsToUploadInfo = (
  fileUrls?: string[],
): NewRequisitionForm["insuranceFileUrls"] =>
  (fileUrls ?? []).reduce<NewRequisitionForm["insuranceFileUrls"]>(
    (files, fileUrl, index) => {
      const uploadFileName = getUploadFileName(fileUrl, index);
      const uploadKey =
        files[uploadFileName] === undefined
          ? uploadFileName
          : `${uploadFileName}-${index + 1}`;
      return {
        ...files,
        [uploadKey]: {
          fileName: fileUrl,
          id: fileUrl,
          valid: true,
          file: fileUrl,
        },
      };
    },
    {},
  );

export const formatOrderingProvider = (name: string, npi: string): string =>
  `${name} - NPI: ${npi}`;

export const convertNewReqTypeToForm = (
  orderedReq: NewRequisitionType,
): NewRequisitionForm => ({
  unityCompleteId: orderedReq.unity_complete_id,
  firstName: orderedReq.first_name,
  middleInitial: orderedReq.middle_initial || "",
  lastName: orderedReq.last_name,
  patientSheetAttached: orderedReq.is_patient_sheet_attached,
  sex: orderedReq.sex,
  maternalWeight:
    orderedReq.maternal_weight === "0.00"
      ? 0
      : Number(orderedReq.maternal_weight ?? 0),
  dateOfBirth: orderedReq.date_of_birth,
  collectionDate: orderedReq.collection_date,
  estimatedDueDate: orderedReq.estimated_due_date || "",
  createdAt: orderedReq.created_at,
  isTwin: orderedReq.is_twin,
  isTripleOrMore: orderedReq.is_triplet_or_more,
  isEggDonor: orderedReq.is_egg_donor,
  ageOfEggDonor: orderedReq.age_of_egg_donor,
  notPregnant: !orderedReq.is_pregnant,
  cellPhone: orderedReq.cell_phone,
  medicalRecordNumber: orderedReq.medical_record_number || "",
  emailAddress: orderedReq.email_address,
  cfDNAOptOut: orderedReq.opt_out_cf_dna,
  fullAddress: "",
  streetAddress: orderedReq.street_address,
  aptUnit: orderedReq.apt_suite,
  city: orderedReq.city,
  state: orderedReq.state,
  zipCode: orderedReq.zip_code,
  isShipToPatient: Boolean(orderedReq.is_ship_to_patient),
  sameAsPatientShippingAddress: !orderedReq.shipping_information,
  shippingAddressTouched: Boolean(orderedReq.shipping_information),
  shippingFirstName: orderedReq.shipping_information?.first_name ?? "",
  shippingLastName: orderedReq.shipping_information?.last_name ?? "",
  shippingFullAddress: "",
  shippingStreetAddress: orderedReq.shipping_information?.street_address ?? "",
  shippingAptUnit: orderedReq.shipping_information?.apt_suite ?? "",
  shippingCity: orderedReq.shipping_information?.city ?? "",
  shippingState: orderedReq.shipping_information?.state ?? "",
  shippingZipCode: orderedReq.shipping_information?.zip_code ?? "",
  asian: orderedReq.is_asian,
  african: orderedReq.is_african,
  ashkenazi: orderedReq.is_ashkenazi,
  middleEastern: orderedReq.is_middle_eastern,
  frenchCanadian: orderedReq.is_french_canadian,
  hispanic: orderedReq.is_hispanic,
  northernEuropean: orderedReq.is_northern_european,
  white: orderedReq.is_white,
  otherEthnicity: orderedReq.is_other_ethnicity,
  unknownEthnicity: orderedReq.is_unknown_ethnicity,
  familyHistoryGeneticDisorders:
    orderedReq.family_history_of_genetic_disorders ?? "",
  reportedCarrierPatientAlpha:
    orderedReq.is_reported_carrier_patient_alpha ?? false,
  reportedCarrierPatientSickle:
    orderedReq.is_reported_carrier_patient_sickle ?? false,
  reportedCarrierPatientSMA:
    orderedReq.is_reported_carrier_patient_SMA ?? false,
  reportedCarrierPatientCF: orderedReq.is_reported_carrier_patient_cf ?? false,
  reportedCarrierPatientVariant:
    orderedReq.reported_carrier_patient_variant ?? "",
  reportedCarrierFOBAlpha: orderedReq.is_reported_carrier_fob_alpha ?? false,
  reportedCarrierFOBSickle: orderedReq.is_reported_carrier_fob_sickle ?? false,
  reportedCarrierFOBSMA: orderedReq.is_reported_carrier_fob_SMA ?? false,
  reportedCarrierFOBCF: orderedReq.is_reported_carrier_fob_cf ?? false,
  reportedCarrierFOBVariant: orderedReq.reported_carrier_fob_variant ?? "",
  reproductivePartnerFirstName: orderedReq.reproductive_partner_first_name,
  reproductivePartnerLastName: orderedReq.reproductive_partner_last_name,
  reproductivePartnerDateOfBirth:
    orderedReq.reproductive_partner_date_of_birth || null,
  maternalReproductivePartner: orderedReq.is_reproductive_partner_maternal,
  paternalReproductivePartner: orderedReq.is_reproductive_partner_paternal,
  patientAcknowledgement: true,
  carrierScreen: orderedReq.is_carrier_screen_order,
  expandedCarrierPanel: orderedReq.expanded_carrier_panel ?? null,
  isRhdOnlyOrder: orderedReq.is_rhd_only_order,
  aneuploidyNIPT: orderedReq.is_aneuploidy_order,
  aneuploidyTwinNIPT: orderedReq.is_aneuploidy_twin_nipt,
  aneuploidyOptOutFetalSex: orderedReq.is_aneuploidy_opt_out_fetal_sex,
  aneuploidyIncludeRhd: orderedReq.is_aneuploidy_include_rhd,
  isFragileX: orderedReq.is_fragile_x,
  isAcogPlus: orderedReq.is_acog_plus,
  isFetalAntigen: orderedReq.is_fetal_antigen,
  isFetalAntigenRbc: orderedReq.is_fetal_antigen_rbc ?? false,
  isFetalAntigenPlatelet: orderedReq.is_fetal_antigen_platelet ?? false,
  isFetalAntigenBigC: orderedReq.is_fetal_antigen_big_c,
  isFetalAntigenLittleC: orderedReq.is_fetal_antigen_little_c,
  isFetalAntigenD: orderedReq.is_fetal_antigen_d,
  isFetalAntigenDuffy: orderedReq.is_fetal_antigen_duffy,
  isFetalAntigenE: orderedReq.is_fetal_antigen_e,
  isFetalAntigenKell: orderedReq.is_fetal_antigen_kell,
  isFetalAntigenLittleE: orderedReq.is_fetal_antigen_little_e ?? false,
  isFetalAntigenFyb: orderedReq.is_fetal_antigen_fyb ?? false,
  isFetalAntigenJka: orderedReq.is_fetal_antigen_jka ?? false,
  isFetalAntigenJkb: orderedReq.is_fetal_antigen_jkb ?? false,
  isFetalAntigenLittleK: orderedReq.is_fetal_antigen_little_k ?? false,
  isFetalAntigenM: orderedReq.is_fetal_antigen_m ?? false,
  isFetalAntigenN: orderedReq.is_fetal_antigen_n ?? false,
  isFetalAntigenBigS: orderedReq.is_fetal_antigen_big_s ?? false,
  isFetalAntigenLittleS: orderedReq.is_fetal_antigen_little_s ?? false,
  isFetalAntigenU: orderedReq.is_fetal_antigen_u ?? false,
  isFetalAntigenHpa1a: orderedReq.is_fetal_antigen_hpa_1a ?? false,
  isFetalAntigenHpa1b: orderedReq.is_fetal_antigen_hpa_1b ?? false,
  isFetalAntigenHpa2a: orderedReq.is_fetal_antigen_hpa_2a ?? false,
  isFetalAntigenHpa2b: orderedReq.is_fetal_antigen_hpa_2b ?? false,
  isFetalAntigenHpa3a: orderedReq.is_fetal_antigen_hpa_3a ?? false,
  isFetalAntigenHpa3b: orderedReq.is_fetal_antigen_hpa_3b ?? false,
  isFetalAntigenHpa4a: orderedReq.is_fetal_antigen_hpa_4a ?? false,
  isFetalAntigenHpa4b: orderedReq.is_fetal_antigen_hpa_4b ?? false,
  isFetalAntigenHpa5a: orderedReq.is_fetal_antigen_hpa_5a ?? false,
  isFetalAntigenHpa5b: orderedReq.is_fetal_antigen_hpa_5b ?? false,
  isFetalAntigenHpa9a: orderedReq.is_fetal_antigen_hpa_9a ?? false,
  isFetalAntigenHpa9b: orderedReq.is_fetal_antigen_hpa_9b ?? false,
  isFetalAntigenHpa15a: orderedReq.is_fetal_antigen_hpa_15a ?? false,
  isFetalAntigenHpa15b: orderedReq.is_fetal_antigen_hpa_15b ?? false,
  insuranceSheetAttached: orderedReq.is_ins_sheet_attached,
  is22q: orderedReq.is_aneuploidy_include_22q,
  vanishingTwin: orderedReq.is_vanishing_twin,
  abnormalUltrasoundFindings: orderedReq.abnormal_ultrasound_findings,
  // icd 10 codes
  Z8481: orderedReq.is_Z84_81,
  Z31430: orderedReq.is_Z31_430,
  Z31440: orderedReq.is_Z31_440,
  Z810: orderedReq.is_Z81_0,
  O283: orderedReq.is_O28_3,
  O285_carrier: orderedReq.is_O28_5_carrier,
  Z3481_carrier: orderedReq.is_Z34_81_carrier,
  Z3482_carrier: orderedReq.is_Z34_82_carrier,
  Z3483_carrier: orderedReq.is_Z34_83_carrier,
  // aneuploidy nipt
  O09511: orderedReq.is_O09_511,
  O09512: orderedReq.is_O09_512,
  O09513: orderedReq.is_O09_513,
  O09521: orderedReq.is_O09_521,
  O09522: orderedReq.is_O09_522,
  O09523: orderedReq.is_O09_523,
  O09891: orderedReq.is_O09_891,
  O09892: orderedReq.is_O09_892,
  O09893: orderedReq.is_O09_893,
  O285_aneuploidy: orderedReq.is_O28_5_aneuploidy,
  Z3481_aneuploidy: orderedReq.is_Z34_81_aneuploidy,
  Z3482_aneuploidy: orderedReq.is_Z34_82_aneuploidy,
  Z3483_aneuploidy: orderedReq.is_Z34_83_aneuploidy,
  Z3401: orderedReq.is_Z34_01,
  Z3402: orderedReq.is_Z34_02,
  Z3403: orderedReq.is_Z34_03,
  Z360: orderedReq.is_Z36_0,
  // rhd nipt
  O360110: orderedReq.is_O36_0110,
  O360120: orderedReq.is_O36_0120,
  O360130: orderedReq.is_O36_0130,
  Z3182: orderedReq.is_Z31_82,
  // fetal antigen nipt
  O361910: orderedReq.is_O36_1910,
  O361920: orderedReq.is_O36_1920,
  O361930: orderedReq.is_O36_1930,
  // (comma separated values if more than one apply)
  otherICDCodes: orderedReq.other_icd_10_codes,
  otherCarrierScreenICDCodes: orderedReq.other_carrier_screen_icd_10_codes,
  otherAneuploidyICDCodes: orderedReq.other_aneuploidy_icd_10_codes,
  otherRhdICDCodes: orderedReq.other_rhd_icd_10_codes,
  otherFetalAntigenICDCodes: orderedReq.other_fetal_antigen_icd_10_codes,
  orderingHealthcareProviders: orderedReq.ordering_health_care_providers,
  orderingHealthcareNPI: orderedReq.npi,
  orderingHealthcareProviderFormatted: formatOrderingProvider(
    orderedReq.ordering_health_care_providers,
    orderedReq.npi,
  ),
  isMobilePhlebotomy: orderedReq.is_mobile_phlebotomy,
  isStandardAccount: orderedReq.is_standard_account,
  isInternationalAccount: orderedReq.is_international_account,
  isMobilePhlebotomyAccount: orderedReq.is_mobile_phlebotomy_account,
  isPatientSheetAccount: orderedReq.is_patient_sheet_account,
  isInsuranceSheetAccount: orderedReq.is_insurance_sheet_account,
  isFetalAntigenAccount: orderedReq.is_fetal_antigen_account,
  isFragileXAccount: orderedReq.is_fragile_x_account,
  isRhdOnlyAccount: orderedReq.is_rhd_only_account,
  is130PanelAccount: orderedReq.is_130_panel_account ?? false,
  is611PanelAccount: orderedReq.is_611_panel_account ?? false,
  is113CustomPanelAccount: orderedReq.is_113_custom_panel_account ?? false,
  providerAuthorization:
    orderedReq.draft_status === DRAFT_STATUS.DRAFT ||
    orderedReq.draft_status === DRAFT_STATUS.REDRAW_DRAFT
      ? (orderedReq.is_provider_authorized ?? false)
      : true,
  clinicName: orderedReq.clinic_name,
  clinicPhone: orderedReq.clinic_phone,
  clinicFax: orderedReq.clinic_fax,
  clinicAccountNumber: orderedReq.lims_id,
  clinicAdditionalNotes: orderedReq.clinic_additional_notes,
  billToInsurance: orderedReq.is_bill_to_insurance,
  billToInsuranceType:
    orderedReq.bill_to_insurance_type ??
    (orderedReq.is_bill_to_insurance ? "Entry" : undefined),
  insuranceCompanyName: orderedReq.insurance_company_name || "",
  groupId: orderedReq.group_id || "",
  memberId: orderedReq.member_id || "",
  policyOwnerFirstName: orderedReq.policy_owner_first_name || "",
  policyOwnerLastName: orderedReq.policy_owner_last_name || "",
  policyOwnerDob: orderedReq.policy_owner_dob || "",
  relationshipToPolicyOwnerSelf: orderedReq.relationship_to_insured === "Self",
  relationshipToPolicyOwnerDependent:
    orderedReq.relationship_to_insured === "Dependent",
  relationshipToPolicyOwnerOther:
    orderedReq.relationship_to_insured === "Other",
  // Cash Pay briefly existed as a separate choice. Normalize saved drafts to
  // Bill to Patient so they require the established patient-billing fields.
  billToPatient:
    orderedReq.is_bill_to_patient || (orderedReq.is_cash_pay ?? false),
  billToPatientEmail: orderedReq.email_address,
  billToClient: orderedReq.is_bill_to_client,
  billToPendingMedicaid: orderedReq.is_pending_medicaid ?? false,
  billToCashPay: false,
  editTreatmentNotes: false,
  treatmentNotes: orderedReq.treatment_notes,
  insuranceFileUrls: convertFileUrlsToUploadInfo(orderedReq.ins_card_urls),
  insuranceFileUrlsSecondaryFront: {},
  insuranceFileUrlsSecondaryBack: {},
  antigenFileUrls: convertFileUrlsToUploadInfo(orderedReq.fetal_antigen_urls),
  disableAneuploidy: false,
  requireFetalAntigenFile:
    orderedReq.is_fetal_antigen &&
    orderedReq.fetal_antigen_urls !== undefined &&
    orderedReq.fetal_antigen_urls.length > 0,
});
